import React, { useState } from "react";
import Login from "./Login";
import Signup from "./Signup";
import { useI18n } from "../../i18n";

export default function AuthContainer({ onAuth }) {
  const { t, lang, setLang } = useI18n();
  const [mode, setMode] = useState("login");
  const [showLangPrompt, setShowLangPrompt] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const downloadLinks = React.useMemo(() => ({
    android: "https://storage.googleapis.com/trackprofit-release/TrackProfit.apk",
    windows: "https://storage.googleapis.com/trackprofit-release/TrackProfit.zip"
  }), []);
  const hasAndroidBuild = Boolean(downloadLinks.android);
  const hasWindowsBuild = Boolean(downloadLinks.windows);

  const scrollTo = (id) => {
    try {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {}
  };
  const goToAuth = (nextMode = "signup") => {
    setMode(nextMode);
    // Scroll after state update
    setTimeout(() => scrollTo("auth"), 0);
  };

  // Check for login route parameter
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const route = params.get('route');
      if (route === 'login') {
        // Check if user is already logged in
        const token = localStorage.getItem('token');
        if (token) {
          // User is already logged in, redirect to dashboard
          window.location.href = '/dashboard';
          return;
        }
        // User is not logged in, show login form
        setMode("login");
        // Scroll to auth section after a short delay to ensure page is loaded
        setTimeout(() => scrollTo("auth"), 100);
      }
    } catch {}
  }, []);

  React.useEffect(() => {
    try {
      const seen = localStorage.getItem('tp_lang_prompt_seen');
      if (!seen && lang !== 'ta') {
        setShowLangPrompt(true);
      }
    } catch {}
  }, [lang]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 py-4 border-b border-green-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={process.env.PUBLIC_URL + '/logo192.png?v=2'} alt="TrackProfit" className="w-8 h-8 rounded" />
          <div className="text-green-700 font-bold">TrackProfit</div>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden sm:flex items-center gap-6 text-sm">
          <button className="bg-green-600 text-white px-3 py-1.5 rounded shadow" onClick={() => goToAuth("signup")}>{t('land.nav.startFree')}</button>
          <button className="text-green-700 hover:underline" onClick={() => goToAuth("login")}>{t('land.nav.login')}</button>
          <button className="text-green-700 hover:underline" onClick={() => scrollTo("downloads")}>{t('land.nav.downloads')}</button>
          <button className="text-green-700 hover:underline" onClick={() => scrollTo("features")}>{t('land.nav.features')}</button>
          <button className="text-green-700 hover:underline" onClick={() => scrollTo("pricing")}>{t('land.nav.pricing')}</button>
          <select
            aria-label="Language"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="text-xs bg-white border border-green-200 text-green-700 px-2 py-1 rounded"
          >
            <option value="en">EN</option>
            <option value="ta">TA</option>
          </select>
        </nav>
        
        {/* Mobile Navigation */}
        <div className="sm:hidden flex items-center gap-3">
          <select
            aria-label="Language"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="text-xs bg-white border border-green-200 text-green-700 px-2 py-1 rounded"
          >
            <option value="en">EN</option>
            <option value="ta">TA</option>
          </select>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-green-700 p-2"
            aria-label="Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-white border-b border-green-100 px-6 py-4">
          <nav className="flex flex-col gap-4">
            <button 
              className="bg-green-600 text-white px-4 py-2 rounded shadow text-left" 
              onClick={() => { goToAuth("signup"); setMobileMenuOpen(false); }}
            >
              {t('land.nav.startFree')}
            </button>
            <button 
              className="text-green-700 hover:underline text-left py-2" 
              onClick={() => { goToAuth("login"); setMobileMenuOpen(false); }}
            >
              {t('land.nav.login')}
            </button>
            <button 
              className="text-green-700 hover:underline text-left py-2" 
              onClick={() => { scrollTo("downloads"); setMobileMenuOpen(false); }}
            >
              {t('land.nav.downloads')}
            </button>
            <button 
              className="text-green-700 hover:underline text-left py-2" 
              onClick={() => { scrollTo("features"); setMobileMenuOpen(false); }}
            >
              {t('land.nav.features')}
            </button>
            <button 
              className="text-green-700 hover:underline text-left py-2" 
              onClick={() => { scrollTo("pricing"); setMobileMenuOpen(false); }}
            >
              {t('land.nav.pricing')}
            </button>
          </nav>
        </div>
      )}

      <main className="flex-1">
        {showLangPrompt && (
          <div className="mx-auto max-w-2xl px-6 mt-4">
            <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl p-3 shadow-sm">
              <div>
                <div className="text-sm font-semibold text-green-700">{t('land.prompt.ta.title')}</div>
                <div className="text-xs text-gray-600">{t('land.prompt.ta.desc')}</div>
              </div>
              <div className="flex items-center gap-2">
                <button className="text-xs bg-white border border-green-200 text-green-700 px-2 py-1 rounded" onClick={() => { try { localStorage.setItem('tp_lang','ta'); } catch {}; setLang('ta'); setShowLangPrompt(false); try { localStorage.setItem('tp_lang_prompt_seen','1'); } catch {} }}>{t('land.prompt.ta.switch')}</button>
                <button className="text-xs text-gray-500" onClick={() => { setShowLangPrompt(false); try { localStorage.setItem('tp_lang_prompt_seen','1'); } catch {} }}>{t('land.prompt.ta.dismiss')}</button>
              </div>
            </div>
          </div>
        )}
        {/* Hero */}
        <section className="px-4 sm:px-6 py-14 sm:py-20 bg-gradient-to-br from-green-50 via-white to-emerald-50">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-green-600/10 text-green-700 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-6">
                <span className="text-lg">✨</span>
                {t('land.hero.badge')}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
                {t('land.hero.title')}
              </h1>
              <p className="text-base sm:text-xl text-gray-700 max-w-2xl mb-3">
                {t('land.hero.p1')}
              </p>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl">
                {t('land.hero.p2')}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  onClick={() => goToAuth("signup")}
                  className="bg-green-600 text-white px-8 py-3 rounded-2xl text-base font-semibold shadow-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <span>🚀</span>
                  {t('land.hero.cta.start')}
                </button>
                <button 
                  type="button"
                  onClick={() => scrollTo("downloads")}
                  className="border-2 border-green-500 text-green-600 px-8 py-3 rounded-2xl text-base font-semibold hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
                >
                  <span>⬇️</span>
                  {t('land.hero.cta.download')}
                </button>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {[
                  { icon: '💻', key: 'land.hero.platform.web' },
                  { icon: '🤖', key: 'land.hero.platform.android' },
                  { icon: '🖥️', key: 'land.hero.platform.windows' },
                  { icon: '💬', key: 'land.hero.platform.whatsapp' },
                ].map((item) => (
                  <div key={item.key} className="inline-flex items-center gap-2 bg-white border border-green-100 text-green-700 px-4 py-2 rounded-full text-xs font-semibold shadow-sm">
                    <span className="text-base">{item.icon}</span>
                    {t(item.key)}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 blur-3xl bg-green-200/60 -z-10 rounded-full" />
              <div className="relative bg-white rounded-[28px] p-6 pb-14 sm:pb-16 shadow-2xl border border-green-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm text-gray-500">{t('land.hero.cards.today')}</div>
                    <div className="text-3xl sm:text-4xl font-bold text-green-600 mt-1 leading-tight">₹2,34,560</div>
                    <div className="text-xs text-gray-400">{t('land.hero.cards.caption')}</div>
                  </div>
                  <div className="bg-green-50 px-4 py-2 rounded-2xl text-sm text-green-700 font-semibold shadow-inner">
                    ⚡ {t('land.hero.stats.orders')}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 min-w-0">
                    <div className="text-xs text-gray-500">{t('land.hero.cards.total')}</div>
                    <div className="text-2xl sm:text-3xl font-bold text-green-700 mt-1 leading-tight break-words">₹18,92,000</div>
                    <div className="text-[11px] text-gray-500 mt-1">{t('land.hero.stats.cash')}</div>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 min-w-0">
                    <div className="text-xs text-gray-500">{t('land.hero.cards.orders')}</div>
                    <div className="text-2xl sm:text-3xl font-bold text-green-700 mt-1 leading-tight break-words">126</div>
                    <div className="text-[11px] text-gray-500 mt-1">{t('land.hero.stats.expenses')}</div>
                  </div>
                </div>
                <div className="mt-6">
                  <div className="text-xs text-gray-500 mb-2">{t('land.hero.cards.caption')}</div>
                  <div className="h-28 bg-gray-50 rounded-2xl border border-gray-100 flex items-end gap-1 p-3">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div
                        key={i}
                        className={`flex-1 rounded-full ${i % 2 ? "bg-green-300" : "bg-green-200"}`}
                        style={{ height: `${35 + i * 5}%` }}
                      />
                    ))}
                  </div>
                </div>
                <div className="absolute -bottom-6 sm:-bottom-8 left-4 right-4 bg-white rounded-2xl shadow-lg border border-green-100 p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">{t('land.hero.stats.orders')}</div>
                    <div className="text-lg font-semibold text-gray-900">32 <span className="text-sm text-green-600">+12%</span></div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">{t('land.hero.stats.cash')}</div>
                    <div className="text-lg font-semibold text-gray-900">₹68K <span className="text-sm text-green-600">+₹8K</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Downloads */}
        <section id="downloads" className="px-4 sm:px-6 py-12 sm:py-16 bg-gradient-to-br from-green-700 via-emerald-600 to-green-500 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/15 text-white px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-4">
                <span>⬇️</span>
                {t('land.download.badge')}
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-3">{t('land.download.title')}</h2>
              <p className="text-sm sm:text-base text-white/90">{t('land.download.subtitle')}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Android Card */}
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 sm:p-7 flex flex-col shadow-2xl">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-white/80">{t('land.download.android.badge')}</div>
                    <h3 className="text-2xl font-semibold text-white mt-1">{t('land.download.android.title')}</h3>
                  </div>
                  <span className="text-4xl">🤖</span>
                </div>
                <p className="text-white/90 text-sm sm:text-base mb-6">{t('land.download.android.desc')}</p>
                <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm text-white/80 mb-6">
                  <div className="bg-white/10 rounded-2xl p-3">
                    <div className="uppercase tracking-wide text-[11px] text-white/70">{t('land.download.metric.size')}</div>
                    <div className="text-white font-semibold mt-1">~7 MB</div>
                  </div>
                  <div className="bg-white/10 rounded-2xl p-3">
                    <div className="uppercase tracking-wide text-[11px] text-white/70">{t('land.download.metric.requirement')}</div>
                    <div className="text-white font-semibold mt-1">Android 8.0+</div>
                  </div>
                </div>
                {hasAndroidBuild ? (
                  <a
                    href={downloadLinks.android}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-white text-green-700 font-semibold px-6 py-3 rounded-2xl shadow-lg hover:translate-y-0.5 transition-transform"
                  >
                    <span className="text-xl">⬇️</span>
                    {t('land.download.cta.android')}
                  </a>
                ) : (
                  <div className="mt-auto">
                    <button
                      type="button"
                      disabled
                      className="w-full inline-flex items-center justify-center gap-2 bg-white/20 text-white/90 font-semibold px-6 py-3 rounded-2xl border border-white/40 cursor-not-allowed"
                    >
                      <span className="text-xl">⏳</span>
                      {t('land.download.comingSoon')}
                    </button>
                    <p className="text-[11px] text-white/75 text-center mt-2">{t('land.download.helperSoon')}</p>
                  </div>
                )}
              </div>

              {/* Windows Card */}
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 sm:p-7 flex flex-col shadow-2xl">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-white/80">{t('land.download.windows.badge')}</div>
                    <h3 className="text-2xl font-semibold text-white mt-1">{t('land.download.windows.title')}</h3>
                  </div>
                  <span className="text-4xl">🖥️</span>
                </div>
                <p className="text-white/90 text-sm sm:text-base mb-6">{t('land.download.windows.desc')}</p>
                <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm text-white/80 mb-6">
                  <div className="bg-white/10 rounded-2xl p-3">
                    <div className="uppercase tracking-wide text-[11px] text-white/70">{t('land.download.metric.size')}</div>
                    <div className="text-white font-semibold mt-1">~180 MB</div>
                  </div>
                  <div className="bg-white/10 rounded-2xl p-3">
                    <div className="uppercase tracking-wide text-[11px] text-white/70">{t('land.download.metric.requirement')}</div>
                    <div className="text-white font-semibold mt-1">Windows 10+</div>
                  </div>
                </div>
                {hasWindowsBuild ? (
                  <a
                    href={downloadLinks.windows}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-white text-green-700 font-semibold px-6 py-3 rounded-2xl shadow-lg hover:translate-y-0.5 transition-transform"
                  >
                    <span className="text-xl">⬇️</span>
                    {t('land.download.cta.windows')}
                  </a>
                ) : (
                  <div className="mt-auto">
                    <button
                      type="button"
                      disabled
                      className="w-full inline-flex items-center justify-center gap-2 bg-white/20 text-white/90 font-semibold px-6 py-3 rounded-2xl border border-white/40 cursor-not-allowed"
                    >
                      <span className="text-xl">⏳</span>
                      {t('land.download.comingSoon')}
                    </button>
                    <p className="text-[11px] text-white/75 text-center mt-2">{t('land.download.helperSoon')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center text-xs sm:text-sm text-white/80 mt-8">
              {t('land.download.helper')}
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="px-6 py-10 bg-gray-50 border-y border-gray-200">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Why Choose TrackProfit?</h2>
              <p className="text-sm text-gray-600">Simple, fast, and accessible from anywhere</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <a 
                href={`https://wa.me/919543268625?text=Hi, I want to try TrackProfit WhatsApp bot`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-gray-200 rounded-xl p-4 relative hover:border-green-300 hover:shadow-md transition-all duration-200 cursor-pointer block"
              >
                <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">NEW</div>
                <div className="text-2xl">📱</div>
                <div className="mt-2 font-semibold text-gray-800">{t('land.features.whatsapp.title')}</div>
                <div className="text-sm text-gray-600">{t('land.features.whatsapp.desc')}</div>
                <div className="mt-3">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors">
                    Try Now →
                  </span>
                </div>
              </a>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl">📈</div>
                <div className="mt-2 font-semibold text-gray-800">{t('land.features.profit.title')}</div>
                <div className="text-sm text-gray-600">{t('land.features.profit.desc')}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl">📑</div>
                <div className="mt-2 font-semibold text-gray-800">{t('land.features.orders.title')}</div>
                <div className="text-sm text-gray-600">{t('land.features.orders.desc')}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl">💸</div>
                <div className="mt-2 font-semibold text-gray-800">{t('land.features.expenses.title')}</div>
                <div className="text-sm text-gray-600">{t('land.features.expenses.desc')}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl">⏱️</div>
                <div className="mt-2 font-semibold text-gray-800">{t('land.features.quick.title')}</div>
                <div className="text-sm text-gray-600">{t('land.features.quick.desc')}</div>
              </div>
            </div>
          </div>
        </section>


        {/* Dashboard Preview Section */}
        <section className="px-6 py-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <span>📊</span>
                Dashboard
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">See Your Business Profit at a Glance</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Access your comprehensive dashboard anytime to view detailed analytics, trends, and insights about your business performance.</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-sm">📈</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">Real-time Analytics</div>
                      <div className="text-sm text-gray-600">Track daily, weekly, and monthly profit trends with interactive charts</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-sm">📑</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">Order Management</div>
                      <div className="text-sm text-gray-600">View all your orders, track status, and analyze performance</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-sm">💸</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">Expense Tracking</div>
                      <div className="text-sm text-gray-600">Categorize and monitor all business expenses with detailed reports</div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-center">
                  <button 
                    onClick={() => goToAuth("signup")}
                    className="bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center gap-2"
                  >
                    <span>📊</span>
                    Access Dashboard
                  </button>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200">
                <div className="text-center mb-4">
                  <div className="text-sm font-semibold text-gray-800 mb-1">Dashboard Preview</div>
                  <div className="text-xs text-gray-500">Your business overview at a glance</div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                    <div className="text-xs text-gray-500">{t('land.hero.cards.today')}</div>
                    <div className="tabular-nums text-lg sm:text-xl font-bold text-green-600">₹2,340</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                    <div className="text-xs text-gray-500">{t('land.hero.cards.total')}</div>
                    <div className="tabular-nums text-lg sm:text-xl font-bold text-green-600">₹18,920</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                    <div className="text-xs text-gray-500">{t('land.hero.cards.orders')}</div>
                    <div className="tabular-nums text-lg sm:text-xl font-bold text-green-600">126</div>
                  </div>
                </div>
                
                <div className="h-24 bg-gray-50 rounded-xl border border-gray-200 flex items-end gap-1 p-2">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className={`flex-1 rounded-sm ${i % 2 ? "bg-green-300" : "bg-green-200"}`} style={{ height: `${30 + i * 8}%` }} />
                  ))}
                </div>
                
                <div className="mt-3 text-xs text-gray-600 text-center">{t('land.hero.cards.caption')}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="px-6 py-10 max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="font-semibold text-gray-800">{t('land.benefits.digit.title')}</div>
              <div className="text-sm text-gray-600">{t('land.benefits.digit.desc')}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="font-semibold text-gray-800">{t('land.benefits.simple.title')}</div>
              <div className="text-sm text-gray-600">{t('land.benefits.simple.desc')}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="font-semibold text-gray-800">{t('land.benefits.anywhere.title')}</div>
              <div className="text-sm text-gray-600">{t('land.benefits.anywhere.desc')}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="font-semibold text-gray-800">{t('land.benefits.cloud.title')}</div>
              <div className="text-sm text-gray-600">{t('land.benefits.cloud.desc')}</div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="px-6 py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Choose Your Plan</h2>
              <p className="text-sm text-gray-600">All plans include WhatsApp bot access</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Free Plan */}
              <div className="border border-gray-200 rounded-2xl p-5 shadow-sm bg-white">
                <div className="text-sm font-semibold text-gray-800">{t('land.pricing.free.title')}</div>
                <div className="mt-1 text-xs text-gray-600">{t('land.pricing.free.desc')}</div>
                <ul className="mt-3 text-sm text-gray-700 space-y-1 list-disc pl-5">
                  <li>{t('land.pricing.free.b1')}</li>
                  <li>{t('land.pricing.free.b2')}</li>
                  <li>{t('land.pricing.free.b3')}</li>
                  <li className="flex items-center gap-1"><span>📱</span> WhatsApp Bot Access</li>
                </ul>
                <button className="mt-4 w-full bg-green-500 text-white py-2 rounded hover:bg-green-600 transition-colors" onClick={() => goToAuth("signup")}>
                  {t('land.pricing.free.cta')}
                </button>
              </div>

              {/* Pro Plan */}
              <div className="border border-gray-200 rounded-2xl p-5 shadow-sm bg-white">
                <div className="text-sm font-semibold text-gray-800">{t('land.pricing.yearly.title')}</div>
                <div className="mt-1 text-xs text-gray-600">{t('land.pricing.yearly.desc')}</div>
                <div className="mt-2 text-2xl font-extrabold text-green-600">{t('land.pricing.yearly.price')}<span className="text-sm font-normal text-gray-600">{t('land.pricing.perYear')}</span></div>
                <div className="text-xs text-gray-500 mt-1">billed annually</div>
                <ul className="mt-3 text-sm text-gray-700 space-y-1 list-disc pl-5">
                  <li>{t('land.pricing.yearly.b1')}</li>
                  <li>{t('land.pricing.yearly.b2')}</li>
                  <li>{t('land.pricing.yearly.b3')}</li>
                  <li className="flex items-center gap-1"><span>📱</span> WhatsApp Bot Access</li>
                </ul>
                <button className="mt-4 w-full bg-green-500 text-white py-2 rounded hover:bg-green-600 transition-colors font-semibold" onClick={() => goToAuth("signup")}>
                  {t('land.pricing.yearly.cta')}
                </button>
              </div>
            </div>

            {/* 3-Year Plan - Featured */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="border-2 border-green-200 rounded-2xl p-6 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm px-4 py-2 rounded-full font-bold shadow-lg">🏆 BEST DEAL</span>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-800">{t('land.pricing.threeyear.title')}</div>
                  <div className="text-sm text-green-600 mt-1">{t('land.pricing.threeyear.desc')}</div>
                  <div className="mt-3">
                    <div className="text-4xl font-extrabold text-green-700 mb-1">{t('land.pricing.threeyear.monthly')}</div>
                    <div className="text-sm text-gray-500">Total: {t('land.pricing.threeyear.price')}</div>
                    <div className="text-sm text-gray-500 line-through mt-1">{t('land.pricing.threeyear.original')}</div>
                  </div>
                  <div className="text-sm text-green-600 font-bold mt-2 bg-green-100 px-3 py-1 rounded-full inline-block">{t('land.pricing.threeyear.b4')}</div>
                </div>
                <ul className="mt-4 text-sm text-gray-700 space-y-1 list-disc pl-5">
                  <li>{t('land.pricing.threeyear.b1')}</li>
                  <li>{t('land.pricing.threeyear.b2')}</li>
                  <li>{t('land.pricing.threeyear.b3')}</li>
                  <li className="flex items-center gap-1"><span>📱</span> WhatsApp Bot Access</li>
                </ul>
                <button className="mt-4 w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all font-bold text-lg shadow-lg" onClick={() => goToAuth("signup")}>
                  {t('land.pricing.threeyear.cta')}
                </button>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <div className="bg-white border border-gray-200 rounded-xl p-4 max-w-md mx-auto">
                <div className="text-sm font-semibold text-gray-800 mb-2">Try WhatsApp Bot First</div>
                <div className="text-xs text-gray-600 mb-3">No signup required - just message our bot</div>
                <a 
                  href={`https://wa.me/919543268625?text=Hi, I want to try TrackProfit WhatsApp bot`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                >
                  <span>📱</span>
                  Message Bot Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Auth section */}
        <section id="auth" className="px-6 py-12 bg-white border-t border-gray-200">
      <div className="max-w-md w-full mx-auto">
            <div className="text-center mb-6">
              <img src={process.env.PUBLIC_URL + '/logo192.png?v=2'} alt="TrackProfit" className="mx-auto w-14 h-14 rounded shadow" />
              <h2 className="mt-3 text-xl font-bold text-gray-800">{mode === "login" ? t('land.auth.heading.login') : t('land.auth.heading.signup')}</h2>
          <p className="text-xs text-gray-500">{mode === "login" ? t('land.auth.sub.login') : t('land.auth.sub.signup')}</p>
        </div>
        {mode === "login" ? (
          <Login onSuccess={onAuth} switchToSignup={() => setMode("signup")} />
        ) : (
          <Signup onSuccess={onAuth} switchToLogin={() => setMode("login")} />
        )}
      </div>
        </section>
      </main>

      <footer className="px-6 py-10 border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto grid gap-6 sm:grid-cols-2 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src={process.env.PUBLIC_URL + '/logo192.png?v=2'} alt="TrackProfit" className="w-8 h-8 rounded" />
              <div className="text-green-600 font-bold">TrackProfit</div>
            </div>
            <div className="text-xs text-gray-600 mb-3">{t('land.footer.tagline')}</div>
            <div className="flex items-center gap-2">
              <a 
                href={`https://wa.me/919543268625?text=Hi, I want to try TrackProfit WhatsApp bot`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 text-white px-3 py-1 rounded text-xs font-medium hover:bg-green-600 transition-colors flex items-center gap-1"
              >
                <span>📱</span>
                WhatsApp Bot
              </a>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800 mb-2">{t('land.footer.about')}</div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li><a href="/terms.html" className="hover:underline">{t('land.footer.terms')}</a></li>
              <li><a href="/privacy.html" className="hover:underline">{t('land.footer.privacy')}</a></li>
              <li><a href="/contact.html" className="hover:underline">{t('land.footer.contact')}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800 mb-2">{t('land.footer.product')}</div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li><button onClick={() => scrollTo("features")} className="hover:underline">{t('land.nav.features')}</button></li>
              <li><button onClick={() => scrollTo("pricing")} className="hover:underline">{t('land.nav.pricing')}</button></li>
              <li><a href={`https://wa.me/919543268625?text=Hi, I want to try TrackProfit WhatsApp bot`} target="_blank" rel="noopener noreferrer" className="hover:underline">WhatsApp Bot</a></li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800 mb-2">Contact</div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li className="flex items-center gap-1">
                <span>📱</span>
                <a href={`https://wa.me/919094107587`} target="_blank" rel="noopener noreferrer" className="hover:underline">+91 90941 07587</a>
              </li>
              <li><a href="/terms.html" className="hover:underline">{t('land.footer.terms')}</a></li>
              <li><a href="/privacy.html" className="hover:underline">{t('land.footer.privacy')}</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-6 text-[11px] text-gray-500 border-t border-gray-200 pt-4 text-center">
          © {new Date().getFullYear()} TrackProfit. All rights reserved.
        </div>
      </footer>
    </div>
  );
}


